import React, { Component } from "react"
import { TextField, SelectField, MenuItem, FontIcon, Toggle } from "material-ui"
import autobind from "class-autobind"
import AppStateStore from "../../../../stores/AppStateStore"
import { patchSpecificAccount } from "../../../../actions/AccountActions"
import strings from "../../../../localization/strings"
import DoubleField from "../../../../components/common/DoubleField"
import * as timeHelper from "../../../../helpers/timeHelper"
import * as api from "../../../../api"
import moment from "moment"
import { EPlanType, ESizeOfBusiness, ETypeOfBusiness, IClientDocument, IAccountDocument, IAccountManagerUserDocument } from "../../../../models"
import AccountManagerClientTabClientDetailsBillingGraph from "./AccountManagerClientTabClientDetailsBillingGraph"
import * as permissionHelper from "../../../../helpers/permissionHelper"
import AuthenticationStore from "../../../../stores/AuthenticationStore"

interface IProps {
    selectedAccount: IAccountDocument
    clientDoc: IClientDocument
    readOnly?: boolean
    reloadData(): void
    updateLoadedAccount(loadedAccount: IAccountDocument): Promise<IAccountDocument>
    updateClientDoc(clientDoc: IClientDocument): void
}

interface IState {
    accountName: string
    accountNameError: string
    accountPlanType: EPlanType
    accountPEPM: number
    accountSignUpDate: string
    accountIndustry: ETypeOfBusiness
    accountSelfReportedEmployeeSize: ESizeOfBusiness
    accountRelationShipManagerId: string
    clientDoc: IClientDocument
    finddTestAccount: boolean
    loadTestDataWeekly: boolean
    accountActive: boolean
    accountManagerUsers: IAccountManagerUserDocument[]
}

export default class AccountManagerClientTabClientDetails extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            accountName: this.props.selectedAccount.Name ? this.props.selectedAccount.Name : "",
            accountNameError: "",
            accountPlanType: this.props.selectedAccount.BillingInfo ? this.props.selectedAccount.BillingInfo.PlanType : null,
            accountPEPM: this.props.selectedAccount.BillingInfo ? this.props.selectedAccount.BillingInfo.PerEmployeePerMonthRate : null,
            accountSignUpDate: moment(timeHelper.keepConvertUTCToLocal(this.props.selectedAccount.ObjectAudit.CreatedDate)).format("MM/DD/YYYY"),
            accountIndustry: this.props.clientDoc.CompanyType,
            accountSelfReportedEmployeeSize: this.props.clientDoc.CompanySize,
            accountRelationShipManagerId: this.props.clientDoc.RelationshipManagerUserId,
            clientDoc: this.props.clientDoc,
            finddTestAccount: this.props.selectedAccount.IsFinddTestAccount,
            loadTestDataWeekly: (this.props.selectedAccount.AccountSettings && this.props.selectedAccount.AccountSettings.FeatureSettings) ? this.props.selectedAccount.AccountSettings.FeatureSettings.LoadTestData : false,
            accountActive: this.props.selectedAccount.Active,
            accountManagerUsers: [],
        }
        autobind(this)
    }

    componentDidMount(): void {
        this.getAccountManagerUsers()
    }

    getAccountManagerUsers() {
        api.getAccountManagerUsers().then(res => this.setState({ accountManagerUsers: res.data as IAccountManagerUserDocument[] }))
	}

    checkValidate() {
        this.setState({ accountNameError: "" })
        let error = false

        if (!this.state.accountName.trim()) {
            this.setState({ accountNameError: strings.NameRequired })
            error = true
        }
        return error
    }

    saveAccountName() {
        !this.checkValidate() &&
            patchSpecificAccount(this.props.selectedAccount.Id, { Name: this.state.accountName }, this.props.selectedAccount.TicksSinceChange, (res) => {
                this.props.reloadData()
                this.props.updateLoadedAccount(res.data as IAccountDocument)
            })
    }

    saveIndustryAndEmployeeSize() {
        if (!this.checkValidate()) {
            const updatedClientDoc = this.state.clientDoc
            updatedClientDoc.CompanyType = this.state.accountIndustry
            updatedClientDoc.CompanySize = this.state.accountSelfReportedEmployeeSize
            AppStateStore.setProgressSnackbarContent("loading")
            api.updateClient(updatedClientDoc).then(res => {
                this.setState({ clientDoc: res.data as IClientDocument }, () => {
                    this.props.updateClientDoc(this.state.clientDoc)
                    AppStateStore.setProgressSnackbarContent("")
                })
            })
        }
    }

    saveAccountRelationshipManager() {
        if (!this.checkValidate()) {
            const updatedClientDoc = this.state.clientDoc
            const relationShipManager = this.state.accountManagerUsers.find(x => x.Id === this.state.accountRelationShipManagerId)
            const relationShipManagerUserName = relationShipManager ? `${relationShipManager.FirstName} ${relationShipManager.LastName}` : null 
            updatedClientDoc.RelationshipManagerUserId = this.state.accountRelationShipManagerId
            updatedClientDoc.RelationshipManagerUserName = relationShipManagerUserName
            AppStateStore.setProgressSnackbarContent("loading")
            api.updateClient(updatedClientDoc).then(resp => {
                this.setState({ clientDoc: resp.data as IClientDocument }, () => {
                    this.props.updateClientDoc(this.state.clientDoc)
                    AppStateStore.setProgressSnackbarContent("")
                })
            })
        }
    }

    handleToggleFinddTestAccount(_event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ finddTestAccount: value }, () =>
                    patchSpecificAccount(this.props.selectedAccount.Id, { IsFinddTestAccount: value }, this.props.selectedAccount.TicksSinceChange, (res) =>
                        this.props.updateLoadedAccount(res.data as IAccountDocument)
                    )
                )
        })
    }

    handleToggleLoadTestData(_event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ loadTestDataWeekly: value }, () =>
                    patchSpecificAccount(this.props.selectedAccount.Id, { AccountSettings: { FeatureSettings: { LoadTestData: value } } }, this.props.selectedAccount.TicksSinceChange, (res) =>
                        this.props.updateLoadedAccount(res.data as IAccountDocument)
                    )
                )
        })
    }

    handleToggleAccountActive(_event, value) {
        AppStateStore.setConfirmDialogContent({
            content: strings.Are_you_sure_you_want_to_change_this_account_setting,
            confirm: () =>
                this.setState({ accountActive: value }, () =>
                    patchSpecificAccount(this.props.selectedAccount.Id, { Active: value }, this.props.selectedAccount.TicksSinceChange, (res) => {
                        this.props.reloadData()
                        this.props.updateLoadedAccount(res.data as IAccountDocument)
                    })
                )
        })
    }

    savePlanType() {
        patchSpecificAccount(this.props.selectedAccount.Id, { BillingInfo: { PlanType: this.state.accountPlanType } }, this.props.selectedAccount.TicksSinceChange, (res) => {
            this.props.reloadData()
            this.props.updateLoadedAccount(res.data as IAccountDocument)
        })
    }

    render() {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        })
        const isRelationshipManagerAllowed = !permissionHelper.hasAccountManagerPermission(AuthenticationStore.AccountManagerUserPermissions.Clients.AssignPotentialClientRelationshipManager)
        return (
            <div style={{ margin: "20px 30px 20px 30px" }}>
                <FontIcon
                    className="fa fa-info-circle"
                    style={{ fontSize: 16, color: "#777777", cursor: "pointer", float: "right" }}
                    onClick={!this.props.readOnly ? e => {
                        e.stopPropagation()
                        AppStateStore.setNoteDrawerContent({
                            drawerTitle: `${strings.Account} "${this.props.selectedAccount.Name}"`,
                            objectData: {
                                ObjectId: this.props.selectedAccount.Id
                            },
                            objectName: this.props.selectedAccount.Name,
                            dataType: "client",
                        })
                    } : null
                    }
                />
                <div style={{ display: "flex" }}>
                    <div style={{ width: 400 }}>
                        <TextField
                            floatingLabelText={strings.AccountName}
                            value={this.state.accountName}
                            errorText={this.state.accountNameError}
                            onChange={(_e, value) => this.setState({ accountName: value }, () => this.checkValidate())}
                            onBlur={this.saveAccountName}
                            style={{ width: 350 }}
                            disabled={this.props.readOnly}
                        />
                        <SelectField
                            floatingLabelText={strings.Plan}
                            value={this.state.accountPlanType}
                            onChange={(_e, _key, accountPlanType) => this.setState({ accountPlanType }, () => this.savePlanType())}
                            style={{ width: 350 }}
                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                        >
                            {Object.keys(EPlanType).filter(key => !isNaN(Number(EPlanType[key]))).map((key, index) => (
                                <MenuItem key={index} value={EPlanType[key]} primaryText={key} />
                            ))}
                        </SelectField>
                        <DoubleField
                            label={strings.Total_PEPM}
                            value={this.state.accountPEPM ? formatter.format(this.state.accountPEPM) : ""}
                            style={{ width: 350, }}
                            readonly
                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                        />
                        <TextField
                            floatingLabelText={strings.SignUp_Date}
                            value={this.state.accountSignUpDate}
                            style={{ width: 350 }}
                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                            disabled
                        />
                        <SelectField
                            floatingLabelText={strings.Industry}
                            value={this.state.accountIndustry}
                            onChange={(_e, _key, accountIndustry) => this.setState({ accountIndustry }, () => this.saveIndustryAndEmployeeSize())}
                            style={{ width: 350 }}
                            disabled={this.props.readOnly}
                        >
                            {Object.keys(ETypeOfBusiness).filter(key => !isNaN(Number(ETypeOfBusiness[key]))).map((key, index) => (
                                <MenuItem key={index} value={ETypeOfBusiness[key]} primaryText={strings[key]} />
                            ))}
                        </SelectField>
                        <SelectField
                            floatingLabelText={strings.Self_Reported_Employee_Size}
                            value={this.state.accountSelfReportedEmployeeSize}
                            onChange={(_e, _key, accountSelfReportedEmployeeSize) => this.setState({ accountSelfReportedEmployeeSize }, () => this.saveIndustryAndEmployeeSize())}
                            style={{ width: 350 }}
                            disabled={this.props.readOnly}
                        >
                            {Object.keys(ESizeOfBusiness).filter(key => !isNaN(Number(ESizeOfBusiness[key]))).map((key, index) => (
                                <MenuItem key={index} value={ESizeOfBusiness[key]} primaryText={strings[key]} />
                            ))}
                        </SelectField>
                        <SelectField
                            floatingLabelText={strings.RelationshipManager}
                            value={this.state.accountRelationShipManagerId}
                            onChange={(_e, _key, accountRelationShipManagerId) => this.setState({ accountRelationShipManagerId }, () => this.saveAccountRelationshipManager())}
                            style={{ width: 350 }}
                            disabled={isRelationshipManagerAllowed ? true : this.props.readOnly}
                        >
                            {this.state.accountManagerUsers.map((item, index) => (
                                <MenuItem key={index} value={item.Id} primaryText={`${item.FirstName} ${item.LastName}`} />
                            ))}
                        </SelectField>
                    </div>
                    <div style={{ width: 600 }}>
                        <AccountManagerClientTabClientDetailsBillingGraph selectedAccount={this.props.selectedAccount} />
                    </div>
                </div>
                <div style={{ float: "right", marginTop: 20 }}>
                    <Toggle
                        toggled={this.state.finddTestAccount}
                        onToggle={this.handleToggleFinddTestAccount}
                        labelPosition="left"
                        label={strings.FinddTestAccount.replace("Findd ", "")}
                        style={{ paddingTop: 10 }}
                        disabled={this.props.readOnly}
                    />
                    <Toggle
                        toggled={this.state.loadTestDataWeekly}
                        onToggle={this.handleToggleLoadTestData}
                        labelPosition="left"
                        label={strings.SalesDemoAccount}
                        style={{ paddingTop: 10 }}
                        disabled={this.props.readOnly}
                    />
                    <Toggle
                        toggled={this.state.accountActive}
                        onToggle={this.handleToggleAccountActive}
                        labelPosition="left"
                        label={strings.AccountActive}
                        style={{ paddingTop: 10 }}
                        disabled={this.props.readOnly}
                    />
                </div>
            </div>
        )
    }
}
